import type { SystemId } from '../emulator/types';
import {
  basename,
  decodeCueBuffer,
  isCueFilename,
  isDiscDataFilename,
  normalizeKey,
  parseCueFileRefs,
  rewriteCueFileRefs,
} from '../emulator/disc/cue';
import {
  buildM3u,
  commonTitleFromDiscNames,
  discLabelFromFilename,
  isM3uFilename,
  parseM3uEntries,
} from '../emulator/disc/m3u';
import { detectSystem } from '../emulator/systems';
import { STORE, del, get, getAll, getAllByIndex, getAllKeysByIndex, put, sha1, tx } from './db';
import { listConflicts, clearConflict } from './conflictRepository';

/** マルチディスクの 1 枚分。 */
export interface RomDiscInfo {
  /** 表示名（Disc 1 など） */
  label: string;
  /** そのディスクの主ファイル（通常 .cue） */
  filename: string;
}

export interface RomMeta {
  id: string;
  /** 表示名（既定は拡張子を落としたファイル名。あとから変更できる） */
  title: string;
  /**
   * 表示名を最後に変更した時刻。
   *
   * サーバー同期は端末をまたいでライブラリのメタを配るので、これが無いと
   * 「リネーム直後に同期が走り、リモートの古い名前で上書きされて元に戻る」
   * ことが起きる。どちらが新しいかを判断するために持つ。
   * 未設定は「一度も変更していない」= 常に負ける、として扱う。
   */
  titleUpdatedAt?: number;
  /** 起動に使う主ファイル名（CUE セットなら .cue、複数ディスクなら .m3u） */
  filename: string;
  system: SystemId;
  size: number;
  sha1: string;
  addedAt: number;
  lastPlayedAt: number | null;
  playCount: number;
  /** 直近のスクリーンショット (data URL)。一覧のサムネイルに使う。 */
  thumbnail?: string;
  favorite?: boolean;
  /**
   * この端末に ROM 本体があるか。
   * false = サーバーからメタだけ同期した状態（プレイには再投入が必要）。
   * 未設定は従来データ扱いで true とみなす。
   */
  hasRomData?: boolean;
  /**
   * ディスクセットの全ファイル名（主ファイル含む）。
   * 未設定 = 単一ファイル ROM。
   */
  discFiles?: string[];
  /**
   * 複数ディスク（2 枚以上）のときだけ持つ。
   * ポーズメニューのディスク交換 UI と、起動主ファイル（.m3u）の対応に使う。
   */
  discs?: RomDiscInfo[];
}

/** サーバー同期用のメタ（端末固有の id は持たない）。 */
export type CloudRomMeta = Omit<RomMeta, 'id' | 'hasRomData'>;

interface RomBlob {
  id: string;
  /** 単一 ROM（ArrayBuffer）。 */
  data?: ArrayBuffer;
  /** 旧形式: 全ファイルを 1 レコードに詰めたもの（非推奨）。 */
  files?: Record<string, ArrayBuffer>;
  /** romParts に分割保存しているときのファイル名一覧。 */
  partNames?: string[];
}

interface RomPart {
  id: string;
  romId: string;
  filename: string;
  /**
   * 同一ファイル内のチャンク番号（0 始まり）。
   * 旧データには無いので、未定義なら 0（＝分割前の 1 個だけ）とみなす。
   */
  index?: number;
  data: ArrayBuffer;
}

/**
 * 1 チャンクの大きさ。
 *
 * 以前はファイル 1 個を丸ごと `await blob.arrayBuffer()` してから
 * 書いていた。600MB の PS1 イメージなら、その瞬間に 600MB を
 * JS ヒープへ載せることになる。iOS ではここで落ち、
 * **ROM が保存されないままライブラリにだけ項目が残る**状態になっていた。
 * 実際に PS1 が起動できなかった原因がこれ。
 *
 * 分割して書けば、常駐はこの大きさで頭打ちになる。
 */
const PART_CHUNK_BYTES = 16 * 1024 * 1024;

export type ImportProgress = {
  phase: 'prepare' | 'save' | 'done';
  /** 処理中のファイル番号（1始まり） */
  current: number;
  /** 保存するファイル総数 */
  total: number;
  filename: string;
  /** セット全体で書き込み済みバイト（分かるとき） */
  bytesDone?: number;
  /** セット全体のバイト数（分かるとき） */
  bytesTotal?: number;
  /** いまのファイル内チャンク（1始まり） */
  chunk?: number;
  /** いまのファイルのチャンク総数 */
  chunks?: number;
};

export class DuplicateRomError extends Error {
  constructor(public existing: RomMeta) {
    super(`「${existing.title}」は既に登録されています`);
    this.name = 'DuplicateRomError';
  }
}

/** ユーザーが「同じゲーム？」確認をキャンセルしたとき。 */
export class ImportCancelledError extends Error {
  constructor() {
    super('追加をキャンセルしました');
    this.name = 'ImportCancelledError';
  }
}

export class UnknownSystemError extends Error {
  constructor(filename: string) {
    super(`${filename} の機種を判別できませんでした`);
    this.name = 'UnknownSystemError';
  }
}

/** 追加時に「既存へ紐づけ / 新規」を尋ねるときの候補情報。 */
export interface AttachPrompt {
  incomingTitle: string;
  incomingFilename: string;
  system: SystemId;
  size: number;
  candidates: RomMeta[];
}

export type AttachChoice =
  | { action: 'attach'; romId: string }
  | { action: 'create' }
  | { action: 'skip' };

export interface AddRomOptions {
  onAttachPrompt?: (prompt: AttachPrompt) => Promise<AttachChoice>;
}

/** 1 ファイルあたりの上限。PS1 の CD イメージを想定して 900MB。 */
const MAX_ROM_BYTES = 900 * 1024 * 1024;
/** セット全体の上限（4 枚組まで想定）。 */
const MAX_SET_BYTES = 3600 * 1024 * 1024;

export async function addRom(
  file: File,
  systemHint?: SystemId,
  options?: AddRomOptions,
): Promise<RomMeta> {
  if (file.size === 0) {
    throw new Error(
      `${file.name} は空です（iCloud 未ダウンロードの可能性。本体を端末に落としてから追加してください）`,
    );
  }
  if (file.size > MAX_ROM_BYTES) {
    throw new Error(`${file.name} が大きすぎます (${formatBytes(file.size)} / 上限 ${formatBytes(MAX_ROM_BYTES)})`);
  }

  // 判定は先頭だけ。保存時に 1 ファイル分だけ読む（セット一括展開はしない）。
  const headBuf = await file.slice(0, 4 * 1024 * 1024 + 0x40).arrayBuffer();
  const head = new Uint8Array(headBuf);
  const system = systemHint ?? detectSystem(file.name, head);
  if (!system) throw new UnknownSystemError(file.name);

  const hash = await fingerprintBlob(file);
  const buffer = await file.arrayBuffer();
  const stem = stripExtension(file.name);
  const all = await getAll<RomMeta>(STORE.roms);

  const target = await resolveAttachTarget(
    all,
    hash,
    system,
    stem,
    file.size,
    options,
    file.name,
  );
  if (target) {
    await deleteRomParts(target.id);
    const meta: RomMeta = {
      ...target,
      filename: file.name,
      title: target.title || stem,
      size: file.size,
      system,
      hasRomData: true,
      // 削除墓標より新しくするため。再投入として扱う。
      addedAt: Date.now(),
      // sha1 はサーバーのセーブ紐づけのため維持
    };
    await tx([STORE.roms, STORE.romData], 'readwrite', (t) => {
      t.objectStore(STORE.roms).put(meta);
      t.objectStore(STORE.romData).put({ id: meta.id, data: buffer } satisfies RomBlob);
    });
    await clearTombstonesForAttach(target.sha1, hash);
    return meta;
  }

  const meta: RomMeta = {
    id: crypto.randomUUID(),
    title: stem,
    filename: file.name,
    system,
    size: file.size,
    sha1: hash,
    addedAt: Date.now(),
    lastPlayedAt: null,
    playCount: 0,
    hasRomData: true,
  };

  await tx([STORE.roms, STORE.romData], 'readwrite', (t) => {
    t.objectStore(STORE.roms).put(meta);
    t.objectStore(STORE.romData).put({ id: meta.id, data: buffer } satisfies RomBlob);
  });

  {
    const { clearTombstone } = await import('./libraryTombstones');
    await clearTombstone(hash);
  }
  return meta;
}

export function romHasLocalData(rom: RomMeta): boolean {
  return rom.hasRomData !== false;
}

function normalizeRomLabel(s: string): string {
  let t = stripExtension(basename(s));
  // スマホの「コピー」「(1)」などを落として照合する
  t = t
    .replace(/\s*[（(]\d+[）)]\s*$/u, '')
    .replace(/\s*[-_]?\s*(copy|コピー|のコピー)\s*$/iu, '')
    .trim();
  return normalizeKey(t);
}

/** タイトル／サイズが近い既存 ROM（指紋不一致のもの）。UI の候補リスト用。 */
export function findSimilarRoms(
  all: RomMeta[],
  hash: string,
  system: SystemId,
  incomingStem: string,
  size: number,
): RomMeta[] {
  const stem = normalizeRomLabel(incomingStem);
  const scored: Array<{ rom: RomMeta; score: number; pending: boolean }> = [];

  for (const rom of all) {
    if (rom.system !== system || rom.sha1 === hash) continue;
    const pending = !romHasLocalData(rom);
    const titleN = normalizeRomLabel(rom.title);
    const fileN = normalizeRomLabel(rom.filename);
    const exactName = Boolean(stem) && (titleN === stem || fileN === stem);
    const sizeMatch = size > 0 && rom.size === size;

    let score = 0;
    if (exactName) {
      // タイトル／ファイル名の完全一致は本体ありでも候補にする
      score += 100;
      if (sizeMatch) score += 20;
    } else if (pending && sizeMatch) {
      // サイズ一致は誤爆しやすいので、ROM 未投入だけに限定
      score += 40;
    } else {
      // 部分一致や「本体あり + サイズだけ」は出さない
      continue;
    }

    scored.push({ rom, score, pending });
  }

  // 未投入を優先し、同点ならスコア順。多くても 3 件まで。
  return scored
    .sort((a, b) => {
      if (a.pending !== b.pending) return a.pending ? -1 : 1;
      return b.score - a.score;
    })
    .slice(0, 3)
    .map((s) => s.rom);
}

/**
 * 紐づけ先を決める。
 * 1) SHA-1 一致（本体あり → DuplicateRomError）
 * 2) 類似候補があり onAttachPrompt があればユーザーに確認
 * 3) プロンプト無しなら、未投入の一意候補だけ自動紐づけ（従来互換）
 */
async function resolveAttachTarget(
  all: RomMeta[],
  hash: string,
  system: SystemId,
  incomingStem: string,
  size: number,
  options?: AddRomOptions,
  incomingFilename = incomingStem,
): Promise<RomMeta | undefined> {
  const bySha = all.find((r) => r.sha1 === hash);
  if (bySha) {
    if (romHasLocalData(bySha)) throw new DuplicateRomError(bySha);
    return bySha;
  }

  const similar = findSimilarRoms(all, hash, system, incomingStem, size);
  if (similar.length > 0 && options?.onAttachPrompt) {
    const choice = await options.onAttachPrompt({
      incomingTitle: incomingStem,
      incomingFilename,
      system,
      size,
      candidates: similar,
    });
    if (choice.action === 'skip') throw new ImportCancelledError();
    if (choice.action === 'attach') {
      const picked = all.find((r) => r.id === choice.romId) ?? similar.find((r) => r.id === choice.romId);
      if (!picked) throw new Error('選択した ROM が見つかりません');
      return picked;
    }
    return undefined; // create
  }

  // UI が無い経路（テスト等）: 未投入の一意候補だけ自動で載せる
  return findAttachTarget(all, hash, system, incomingStem, size);
}

/**
 * 本体の再投入先を探す（自動・未投入のみ）。
 * 1) SHA-1 一致
 * 2) ROM 未投入かつタイトル/ファイル名が一致（候補がちょうど 1 件）
 * 3) ROM 未投入かつサイズ一致（候補がちょうど 1 件）
 */
function findAttachTarget(
  all: RomMeta[],
  hash: string,
  system: SystemId,
  incomingStem: string,
  size: number,
): RomMeta | undefined {
  const bySha = all.find((r) => r.sha1 === hash);
  if (bySha) return bySha;

  const pending = all.filter((r) => !romHasLocalData(r) && r.system === system);

  const stem = normalizeRomLabel(incomingStem);
  if (stem) {
    const byName = pending.filter(
      (r) => normalizeRomLabel(r.title) === stem || normalizeRomLabel(r.filename) === stem,
    );
    if (byName.length === 1) return byName[0];
  }

  if (size > 0) {
    const bySize = pending.filter((r) => r.size === size);
    if (bySize.length === 1) return bySize[0];
  }

  return undefined;
}

async function clearTombstonesForAttach(cloudSha1: string, localHash: string): Promise<void> {
  const { clearTombstone } = await import('./libraryTombstones');
  await clearTombstone(cloudSha1);
  if (localHash !== cloudSha1) await clearTombstone(localHash);
}

/** サーバーから受け取ったメタをローカルへ反映（本体は付けない）。 */
export async function upsertCloudRomMeta(entry: CloudRomMeta): Promise<RomMeta> {
  const all = await getAll<RomMeta>(STORE.roms);
  const existing = all.find((r) => r.sha1 === entry.sha1);
  if (existing) {
    // タイトルは「最後に変更した方」を採る。無条件にリモートを採ると、
    // 手元でリネームした直後の同期で古い名前に戻ってしまう。
    const remoteTitleAt = entry.titleUpdatedAt ?? 0;
    const localTitleAt = existing.titleUpdatedAt ?? 0;
    const takeRemoteTitle = Boolean(entry.title) && remoteTitleAt >= localTitleAt;

    const next: RomMeta = {
      ...existing,
      title: takeRemoteTitle ? entry.title : existing.title,
      ...(takeRemoteTitle
        ? entry.titleUpdatedAt !== undefined
          ? { titleUpdatedAt: entry.titleUpdatedAt }
          : {}
        : existing.titleUpdatedAt !== undefined
          ? { titleUpdatedAt: existing.titleUpdatedAt }
          : {}),
      filename: entry.filename || existing.filename,
      system: entry.system,
      size: entry.size || existing.size,
      addedAt: Math.min(existing.addedAt, entry.addedAt || existing.addedAt),
      lastPlayedAt: maxNullable(existing.lastPlayedAt, entry.lastPlayedAt),
      playCount: Math.max(existing.playCount, entry.playCount ?? 0),
      thumbnail: entry.thumbnail || existing.thumbnail,
      favorite: Boolean(existing.favorite || entry.favorite),
      hasRomData: romHasLocalData(existing),
      ...(entry.discFiles?.length
        ? { discFiles: entry.discFiles }
        : existing.discFiles?.length
          ? { discFiles: existing.discFiles }
          : {}),
    };
    await put(STORE.roms, next);
    return next;
  }

  const meta: RomMeta = {
    id: crypto.randomUUID(),
    title: entry.title,
    filename: entry.filename,
    system: entry.system,
    size: entry.size,
    sha1: entry.sha1,
    addedAt: entry.addedAt || Date.now(),
    lastPlayedAt: entry.lastPlayedAt ?? null,
    playCount: entry.playCount ?? 0,
    ...(entry.thumbnail ? { thumbnail: entry.thumbnail } : {}),
    ...(entry.favorite ? { favorite: true } : {}),
    ...(entry.discFiles?.length ? { discFiles: entry.discFiles } : {}),
    hasRomData: false,
  };
  await put(STORE.roms, meta);
  return meta;
}

export function toCloudRomMeta(rom: RomMeta): CloudRomMeta {
  return {
    title: rom.title,
    filename: rom.filename,
    system: rom.system,
    size: rom.size,
    sha1: rom.sha1,
    addedAt: rom.addedAt,
    lastPlayedAt: rom.lastPlayedAt,
    playCount: rom.playCount,
    ...(rom.titleUpdatedAt !== undefined ? { titleUpdatedAt: rom.titleUpdatedAt } : {}),
    ...(rom.thumbnail ? { thumbnail: rom.thumbnail } : {}),
    ...(rom.favorite ? { favorite: true } : {}),
    ...(rom.discFiles?.length ? { discFiles: [...rom.discFiles] } : {}),
  };
}

function maxNullable(a: number | null, b: number | null | undefined): number | null {
  if (a == null) return b ?? null;
  if (b == null) return a;
  return Math.max(a, b);
}

/** ドラッグ&ドロップ / ファイル選択でまとめて追加する。 */
export interface ImportResult {
  added: RomMeta[];
  skipped: Array<{ filename: string; reason: string }>;
}

export async function addRoms(
  files: FileList | File[],
  onProgress?: (p: ImportProgress) => void,
  options?: AddRomOptions,
): Promise<ImportResult> {
  const result: ImportResult = { added: [], skipped: [] };
  const list = Array.from(files);
  const byKey = new Map<string, File>();
  for (const file of list) {
    byKey.set(normalizeKey(file.name), file);
  }

  const consumed = new Set<File>();
  const totalSteps = Math.max(1, list.length);

  onProgress?.({
    phase: 'prepare',
    current: 0,
    total: totalSteps,
    filename: list[0]?.name ?? '',
  });

  const cueFiles = list.filter((f) => isCueFilename(f.name));
  const discFiles = list.filter((f) => isDiscDataFilename(f.name));
  const m3uFiles = list.filter((f) => isM3uFilename(f.name));

  /** addRomSet からのバイト進捗をそのまま UI へ渡す（チャンクごとに呼ばれる） */
  const progressCb = (p: ImportProgress) => {
    onProgress?.(p);
  };

  // 明示的な .m3u（参照先の CUE/BIN を同じ選択に含める）
  for (const m3uFile of m3uFiles) {
    try {
      const meta = await importM3uSet(m3uFile, list, consumed, byKey, progressCb, options);
      result.added.push(meta);
    } catch (e) {
      consumed.add(m3uFile);
      result.skipped.push({
        filename: m3uFile.name,
        reason: e instanceof Error ? e.message : String(e),
      });
    }
  }

  // 複数 CUE をまとめて選んだ → 1 タイトルのマルチディスクとして登録
  const remainingCues = cueFiles.filter((f) => !consumed.has(f));
  if (remainingCues.length > 1 && m3uFiles.length === 0) {
    try {
      const meta = await importMultiCueSet(remainingCues, list, consumed, byKey, progressCb, options);
      result.added.push(meta);
    } catch (e) {
      for (const f of remainingCues) consumed.add(f);
      result.skipped.push({
        filename: remainingCues.map((f) => f.name).join(', '),
        reason: e instanceof Error ? e.message : String(e),
      });
    }
  } else if (remainingCues.length === 1 && discFiles.some((f) => !consumed.has(f))) {
    // CUE 1 + BIN: 同梱 BIN は必ず 1 セットにする
    const cueFile = remainingCues[0]!;
    try {
      const cueBuf = await cueFile.arrayBuffer();
      const cueText = decodeCueBuffer(cueBuf)[0] ?? '';
      const bins = discFiles
        .filter((f) => !consumed.has(f))
        .sort((a, b) =>
          basename(a.name).localeCompare(basename(b.name), undefined, { numeric: true }),
        );
      const resolved = buildRewrittenSet(cueFile, cueText, bins);
      for (const f of resolved.files.values()) consumed.add(f);
      result.added.push(
        await addRomSet(resolved.files, basename(cueFile.name), resolved.cueOverride, progressCb, options),
      );
    } catch (e) {
      for (const f of [cueFile, ...discFiles.filter((d) => !consumed.has(d))]) consumed.add(f);
      result.skipped.push({
        filename: cueFile.name,
        reason: e instanceof Error ? e.message : String(e),
      });
    }
  } else if (remainingCues.length === 1) {
    const cueFile = remainingCues[0]!;
    try {
      if (cueFile.size === 0) {
        throw new Error(
          `${cueFile.name} は空です（iCloud 未ダウンロードの可能性。本体を端末に落としてから追加してください）`,
        );
      }
      const cueBuf = await cueFile.arrayBuffer();
      const resolved = resolveCueSet(cueBuf, cueFile, list, consumed, byKey);
      if (!resolved) {
        consumed.add(cueFile);
        result.skipped.push({
          filename: cueFile.name,
          reason: '対応する .bin が見つかりません。.cue と .bin をまとめて選んでください',
        });
      } else {
        for (const f of resolved.files.values()) consumed.add(f);
        result.added.push(
          await addRomSet(
            resolved.files,
            basename(cueFile.name),
            resolved.cueOverride,
            progressCb,
            options,
          ),
        );
      }
    } catch (e) {
      consumed.add(cueFile);
      result.skipped.push({
        filename: cueFile.name,
        reason: e instanceof Error ? e.message : String(e),
      });
    }
  }

  let singleIndex = 0;
  const singles = list.filter((file) => {
    if (consumed.has(file)) return false;
    if (
      (cueFiles.length > 0 || m3uFiles.length > 0) &&
      (isDiscDataFilename(file.name) || isCueFilename(file.name) || isM3uFilename(file.name))
    ) {
      result.skipped.push({
        filename: file.name,
        reason: '同じ選択内のプレイリスト／.cue に含められませんでした（ファイル名を確認してください）',
      });
      return false;
    }
    return true;
  });
  for (const file of singles) {
    singleIndex++;
    try {
      onProgress?.({
        phase: 'save',
        current: singleIndex,
        total: singles.length,
        filename: file.name,
        bytesDone: 0,
        bytesTotal: file.size,
      });
      result.added.push(await addRom(file, undefined, options));
      onProgress?.({
        phase: 'save',
        current: singleIndex,
        total: singles.length,
        filename: file.name,
        bytesDone: file.size,
        bytesTotal: file.size,
      });
    } catch (e) {
      result.skipped.push({
        filename: file.name,
        reason: e instanceof Error ? e.message : String(e),
      });
    }
  }

  onProgress?.({ phase: 'done', current: totalSteps, total: totalSteps, filename: '' });
  return result;
}

/**
 * .m3u と参照先ファイルを 1 エントリにまとめる。
 */
async function importM3uSet(
  m3uFile: File,
  batch: File[],
  consumed: Set<File>,
  byKey: Map<string, File>,
  onProgress?: (p: ImportProgress) => void,
  options?: AddRomOptions,
): Promise<RomMeta> {
  if (consumed.has(m3uFile)) {
    throw new Error(`${m3uFile.name} は既に取り込み済みです`);
  }
  const text = decodeCueBuffer(await m3uFile.arrayBuffer())[0] ?? '';
  const entries = parseM3uEntries(text);
  if (entries.length === 0) {
    throw new Error(`${m3uFile.name} に有効なエントリがありません`);
  }

  const files = new Map<string, File>();
  files.set(basename(m3uFile.name), m3uFile);
  consumed.add(m3uFile);

  const discs: RomDiscInfo[] = [];
  for (let i = 0; i < entries.length; i++) {
    const entryName = entries[i]!;
    const hit = byKey.get(normalizeKey(entryName));
    if (!hit || consumed.has(hit)) {
      throw new Error(
        `${m3uFile.name} が参照する ${entryName} が見つかりません。.m3u と各ディスクをまとめて選んでください`,
      );
    }
    if (isCueFilename(hit.name)) {
      const cueBuf = await hit.arrayBuffer();
      const resolved = resolveCueSet(cueBuf, hit, batch, consumed, byKey);
      if (!resolved) {
        throw new Error(`${hit.name} に対応する .bin が見つかりません`);
      }
      for (const [name, f] of resolved.files) {
        if (!files.has(name)) files.set(name, f);
        consumed.add(f);
      }
      // CUE 書き換えがある場合は上書き用バイトを別途持たせる（下で merge）
      if (resolved.cueOverride) {
        files.set(
          basename(hit.name),
          new File([resolved.cueOverride.slice()], basename(hit.name), { type: 'text/plain' }),
        );
      }
      discs.push({ label: discLabelFromFilename(hit.name, i), filename: basename(hit.name) });
    } else {
      files.set(basename(hit.name), hit);
      consumed.add(hit);
      discs.push({ label: discLabelFromFilename(hit.name, i), filename: basename(hit.name) });
    }
  }

  return addRomSet(
    files,
    basename(m3uFile.name),
    undefined,
    onProgress,
    options,
    discs.length > 1 ? discs : undefined,
  );
}

/**
 * 複数 CUE を 1 タイトルのマルチディスクとして登録する。
 * 生成した .m3u を主ファイルにし、pcsx の Disk Control で切替できるようにする。
 */
async function importMultiCueSet(
  cueFiles: File[],
  batch: File[],
  consumed: Set<File>,
  byKey: Map<string, File>,
  onProgress?: (p: ImportProgress) => void,
  options?: AddRomOptions,
): Promise<RomMeta> {
  const sorted = [...cueFiles].sort((a, b) =>
    basename(a.name).localeCompare(basename(b.name), undefined, { numeric: true }),
  );
  const files = new Map<string, File>();
  const discs: RomDiscInfo[] = [];
  const cueOverrides = new Map<string, Uint8Array>();

  for (let i = 0; i < sorted.length; i++) {
    const cueFile = sorted[i]!;
    if (consumed.has(cueFile)) continue;
    if (cueFile.size === 0) {
      throw new Error(
        `${cueFile.name} は空です（iCloud 未ダウンロードの可能性。本体を端末に落としてから追加してください）`,
      );
    }
    const cueBuf = await cueFile.arrayBuffer();
    const resolved = resolveCueSet(cueBuf, cueFile, batch, consumed, byKey);
    if (!resolved) {
      throw new Error(`${cueFile.name} に対応する .bin が見つかりません`);
    }
    for (const [name, f] of resolved.files) {
      if (!files.has(name)) files.set(name, f);
      consumed.add(f);
    }
    if (resolved.cueOverride) {
      cueOverrides.set(basename(cueFile.name), resolved.cueOverride);
    }
    discs.push({ label: discLabelFromFilename(cueFile.name, i), filename: basename(cueFile.name) });
  }

  if (discs.length < 2) {
    throw new Error('マルチディスクにするには .cue が 2 枚以上必要です');
  }

  const title = commonTitleFromDiscNames(discs.map((d) => d.filename));
  const playlistName = `${title}.m3u`;
  const m3uBody = buildM3u(discs.map((d) => d.filename));
  files.set(
    playlistName,
    new File([new TextEncoder().encode(m3uBody)], playlistName, { type: 'audio/x-mpegurl' }),
  );

  // CUE 書き換えを File に反映（addRomSet は cueOverride を主ファイルにしか当てない）
  for (const [name, bytes] of cueOverrides) {
    files.set(name, new File([bytes.slice()], name, { type: 'text/plain' }));
  }

  return addRomSet(files, playlistName, undefined, onProgress, options, discs, title);
}

/**
 * CUE とバッチ内ファイルを突き合わせる。
 * 1) 参照名の完全一致（大小・全角半角吸収）
 * 2) 参照数と BIN 数が一致 → 名前順で紐づけ＋ CUE 書き換え
 * 3) バッチが「CUE 1 + ディスク実体」だけ → 全部紐づけ＋ CUE 書き換え
 */
function resolveCueSet(
  cueBuf: ArrayBuffer,
  cueFile: File,
  batch: File[],
  consumed: Set<File>,
  byKey: Map<string, File>,
): { files: Map<string, File>; cueOverride?: Uint8Array } | null {
  const availableBins = () =>
    batch
      .filter((f) => !consumed.has(f) && f !== cueFile && isDiscDataFilename(f.name))
      .sort((a, b) => basename(a.name).localeCompare(basename(b.name), undefined, { numeric: true }));

  for (const cueText of decodeCueBuffer(cueBuf)) {
    const refs = parseCueFileRefs(cueText);
    if (refs.length === 0) continue;

    // 1) 名前一致
    const matched = new Map<string, File>();
    const missing: string[] = [];
    for (const ref of refs) {
      const hit = byKey.get(normalizeKey(ref));
      if (hit && !consumed.has(hit)) matched.set(basename(hit.name), hit);
      else missing.push(ref);
    }
    if (missing.length === 0) {
      const files = new Map<string, File>();
      files.set(basename(cueFile.name), cueFile);
      for (const [name, f] of matched) files.set(name, f);
      return { files };
    }

    // 2) 個数が一致するなら名前順で紐づけ
    const bins = availableBins();
    if (bins.length === refs.length && bins.length > 0) {
      return buildRewrittenSet(cueFile, cueText, bins);
    }
  }

  // 3) このバッチが CUE1本 + 実体だけなら強引にセット化
  const others = batch.filter((f) => f !== cueFile && !consumed.has(f));
  const bins = others.filter((f) => isDiscDataFilename(f.name));
  if (others.length > 0 && bins.length === others.length && bins.length > 0) {
    const cueText = decodeCueBuffer(cueBuf)[0] ?? '';
    return buildRewrittenSet(
      cueFile,
      cueText || 'FILE "track.bin" BINARY\n  TRACK 01 MODE2/2352\n    INDEX 01 00:00:00\n',
      bins.sort((a, b) =>
        basename(a.name).localeCompare(basename(b.name), undefined, { numeric: true }),
      ),
    );
  }

  const sample = decodeCueBuffer(cueBuf)[0] ?? '';
  const refs = parseCueFileRefs(sample);
  if (refs.length === 0) return null;

  throw new Error(
    `参照ファイルが見つかりません: ${refs.join(', ')}（.cue と .bin を同じ操作でまとめて追加してください。ファイル名が CUE 内と一致しているかも確認）`,
  );
}

function buildRewrittenSet(
  cueFile: File,
  cueText: string,
  bins: File[],
): { files: Map<string, File>; cueOverride: Uint8Array } {
  const names = bins.map((b) => basename(b.name));
  const rewritten = rewriteCueFileRefs(
    cueText.includes('FILE') ? cueText : names.map((n) => `FILE "${n}" BINARY`).join('\n') + '\n',
    names,
  );
  const files = new Map<string, File>();
  files.set(basename(cueFile.name), cueFile);
  for (const b of bins) files.set(basename(b.name), b);
  return { files, cueOverride: new TextEncoder().encode(rewritten) };
}

/**
 * CUE+BIN など複数ファイルを 1 ROM エントリとして保存する。
 * 巨大 BIN を全部 RAM に載せないよう、Blob のまま 1 ファイルずつ IndexedDB へ書く。
 */
export async function addRomSet(
  files: Map<string, File> | Record<string, File>,
  primaryName: string,
  cueOverride?: Uint8Array,
  onProgress?: (p: ImportProgress) => void,
  options?: AddRomOptions,
  discs?: RomDiscInfo[],
  titleOverride?: string,
): Promise<RomMeta> {
  const map = files instanceof Map ? files : new Map(Object.entries(files));
  const primaryKey = basename(primaryName);
  const primaryFile = map.get(primaryKey) ?? [...map.values()].find(
    (f) => normalizeKey(f.name) === normalizeKey(primaryKey),
  );
  if (!primaryFile) throw new Error(`主ファイル ${primaryName} がセットにありません`);

  const entries: Array<{ name: string; source: Blob }> = [];
  let totalSize = 0;
  for (const [, file] of map) {
    const name = basename(file.name);
    if (file.size === 0 && !(cueOverride && isCueFilename(name))) {
      throw new Error(
        `${name} は空です（iCloud 未ダウンロードの可能性。本体を端末に落としてから追加してください）`,
      );
    }
    if (file.size > MAX_ROM_BYTES) {
      throw new Error(`${name} が大きすぎます (${formatBytes(file.size)} / 上限 ${formatBytes(MAX_ROM_BYTES)})`);
    }
    totalSize += Math.max(file.size, cueOverride && isCueFilename(name) ? cueOverride.byteLength : 0);
    if (cueOverride && isCueFilename(name) && normalizeKey(name) === normalizeKey(primaryKey)) {
      entries.push({ name, source: new Blob([cueOverride.slice()]) });
    } else {
      entries.push({ name, source: file });
    }
  }
  if (totalSize > MAX_SET_BYTES) {
    throw new Error(`ディスクセットが大きすぎます (${formatBytes(totalSize)})`);
  }

  const system =
    detectSystem(primaryFile.name, new Uint8Array(0)) ??
    (isCueFilename(primaryFile.name) || isM3uFilename(primaryFile.name) ? 'psx' : null);
  if (!system) throw new UnknownSystemError(primaryFile.name);

  // 指紋は先頭/末尾だけ。全文ハッシュは iPhone で落ちる。
  const hash = await fingerprintDiscSet(entries);
  const discFiles = entries.map((e) => e.name).sort((a, b) => a.localeCompare(b));
  const stem = titleOverride || stripExtension(primaryFile.name);
  const all = await getAll<RomMeta>(STORE.roms);

  const existing = await resolveAttachTarget(
    all,
    hash,
    system,
    stem,
    totalSize,
    options,
    basename(primaryFile.name),
  );

  const meta: RomMeta = {
    id: existing?.id ?? crypto.randomUUID(),
    title: existing?.title || stem,
    filename: basename(primaryFile.name),
    system,
    size: totalSize,
    // サーバーのセーブ紐づけのため、既存メタの sha1 を優先して維持する
    sha1: existing?.sha1 ?? hash,
    addedAt: Date.now(),
    lastPlayedAt: existing?.lastPlayedAt ?? null,
    playCount: existing?.playCount ?? 0,
    hasRomData: true,
    discFiles,
    ...(discs && discs.length > 1 ? { discs } : existing?.discs ? { discs: existing.discs } : {}),
    ...(existing?.thumbnail ? { thumbnail: existing.thumbnail } : {}),
    ...(existing?.favorite ? { favorite: existing.favorite } : {}),
  };

  // メタを先に書き、続けてパートを逐次保存（同時に複数 BIN を RAM に載せない）
  await tx([STORE.roms, STORE.romData], 'readwrite', (t) => {
    t.objectStore(STORE.roms).put(meta);
    t.objectStore(STORE.romData).put({
      id: meta.id,
      partNames: discFiles,
    } satisfies RomBlob);
  });

  await deleteRomParts(meta.id);

  let i = 0;
  let bytesDone = 0;
  try {
    for (const { name, source } of entries) {
      i++;
      const chunks = Math.max(1, Math.ceil(source.size / PART_CHUNK_BYTES));
      onProgress?.({
        phase: 'save',
        current: i,
        total: entries.length,
        filename: name,
        bytesDone,
        bytesTotal: totalSize,
        chunk: 0,
        chunks,
      });
      // 分割して書く。一度に載るのは 1 チャンクぶんだけ。
      for (let c = 0; c < chunks; c++) {
        const start = c * PART_CHUNK_BYTES;
        const end = Math.min(start + PART_CHUNK_BYTES, source.size);
        const data = await source.slice(start, end).arrayBuffer();
        await put(STORE.romParts, {
          id: partId(meta.id, name, c),
          romId: meta.id,
          filename: name,
          index: c,
          data,
        } satisfies RomPart);
        bytesDone += end - start;
        onProgress?.({
          phase: 'save',
          current: i,
          total: entries.length,
          filename: name,
          bytesDone,
          bytesTotal: totalSize,
          chunk: c + 1,
          chunks,
        });
        // チャンクごとにタスクを譲り、参照を手放す隙を作る
        await new Promise((r) => setTimeout(r, 0));
      }
    }

    // 全部揃ったことを必ず確かめる。
    // ここを省くと、途中で失敗しても「ライブラリには出るが起動できない」
    // 項目が黙って残る。実際にそれで PS1 が起動できなくなっていた。
    const missing = await missingPartNames(meta.id, discFiles);
    if (missing.length > 0) {
      throw new Error(`保存できなかったファイルがあります: ${missing.join(', ')}`);
    }
  } catch (e) {
    // 中途半端な状態を残さない。本体無しとして記録し、再追加を促す。
    await markRomDataLost(meta.id);
    const reason = e instanceof Error ? e.message : String(e);
    throw new Error(
      `${meta.title} の保存に失敗しました: ${reason}\n` +
        '端末の空き容量を確認して、もう一度追加してください。',
    );
  }

  await clearTombstonesForAttach(meta.sha1, hash);
  return meta;
}

/** discFiles のうち、実際にパートが無いものを返す。 */
async function missingPartNames(romId: string, expected: string[]): Promise<string[]> {
  const parts = await getAllByIndex<RomPart>(STORE.romParts, 'romId', romId);
  const have = new Set(parts.map((p) => p.filename));
  return expected.filter((name) => !have.has(name));
}

/** 本体を失った（または保存できなかった）ことを記録する。 */
async function markRomDataLost(romId: string): Promise<void> {
  await deleteRomParts(romId);
  try {
    await tx([STORE.roms, STORE.romData], 'readwrite', (t) => {
      t.objectStore(STORE.romData).delete(romId);
      const req = t.objectStore(STORE.roms).get(romId);
      req.onsuccess = () => {
        const meta = req.result as RomMeta | undefined;
        if (meta) t.objectStore(STORE.roms).put({ ...meta, hasRomData: false });
      };
    });
  } catch {
    /* 後始末で失敗しても、元のエラーを潰さない */
  }
}

function partId(romId: string, filename: string, index: number): string {
  // 旧データは添字なしの ID なので、0 番だけ従来と同じ形にして重複を避ける
  return index === 0 ? `${romId}:${filename}` : `${romId}:${filename}#${index}`;
}

/** パート群をファイル名ごとにまとめ、チャンク順に Blob へ組み直す。 */
function mergePieceBlobs(
  byName: Map<string, Array<{ index: number; blob: Blob }>>,
): Record<string, Blob> {
  const out: Record<string, Blob> = {};
  for (const [name, list] of byName) {
    list.sort((a, b) => a.index - b.index);
    out[name] = list.length === 1 ? list[0]!.blob : new Blob(list.map((p) => p.blob));
  }
  return out;
}

function yieldToUi(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export interface RomBlobLoadProgress {
  /** 読み終わったパート数 */
  done: number;
  /** パート総数（不明なら 0） */
  total: number;
  label: string;
}

/**
 * 起動中の ROM 組み立て。キャンセルで Abort し、次回は必ず新規に読み直す。
 * （完了前の Promise を共有すると、キャンセル後の再起動が同じ重い処理に張り付く）
 */
type InflightEntry = {
  controller: AbortController;
  promise: Promise<Record<string, Blob>>;
};
const romBlobInflight = new Map<string, InflightEntry>();

/** IndexedDB 上の Blob をそのまま返す（起動時に 1 ファイルずつ WASM FS へ書く用）。 */
export async function getRomFileBlobs(
  id: string,
  onProgress?: (p: RomBlobLoadProgress) => void,
  signal?: AbortSignal,
): Promise<Record<string, Blob>> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  // 外部 signal が付いた呼び出しは共有しない（セッション単位のキャンセル用）
  if (!signal) {
    const existing = romBlobInflight.get(id);
    if (existing && !existing.controller.signal.aborted) {
      return existing.promise;
    }
  }

  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener('abort', onExternalAbort, { once: true });
    }
  }

  const pending = loadRomFileBlobs(id, onProgress, controller.signal).finally(() => {
    signal?.removeEventListener('abort', onExternalAbort);
    const cur = romBlobInflight.get(id);
    if (cur?.promise === pending) romBlobInflight.delete(id);
  });

  // signal 無し（エクスポート等）だけ共有表に載せる
  if (!signal) {
    romBlobInflight.set(id, { controller, promise: pending });
  }

  return pending;
}

/**
 * 進行中の ROM 組み立てを打ち切る。キャンセル直後の再起動が
 * 古い読み出しに張り付かないようにする。
 */
export function abortRomFileBlobs(id: string): void {
  const entry = romBlobInflight.get(id);
  if (!entry) return;
  entry.controller.abort();
  romBlobInflight.delete(id);
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
}

async function loadRomFileBlobs(
  id: string,
  onProgress?: (p: RomBlobLoadProgress) => void,
  signal?: AbortSignal,
): Promise<Record<string, Blob>> {
  throwIfAborted(signal);
  const record = await get<RomBlob>(STORE.romData, id);
  throwIfAborted(signal);
  if (!record) throw new Error('ROM データが見つかりません（削除された可能性があります）');

  if (record.partNames && record.partNames.length > 0) {
    // getAll で全パートを一度に載せると PS1 級でメインが固まる／落ちる。
    // キーだけ取り、1 パートずつ読む。
    const keys = await getAllKeysByIndex(STORE.romParts, 'romId', id);
    throwIfAborted(signal);
    if (keys.length === 0) {
      throw new Error('ROM パートが見つかりません。もう一度追加してください');
    }

    const byName = new Map<string, Array<{ index: number; blob: Blob }>>();
    const total = keys.length;
    let done = 0;

    for (const key of keys) {
      throwIfAborted(signal);
      const part = await get<RomPart>(STORE.romParts, key);
      throwIfAborted(signal);
      if (!part) continue;

      const blob = new Blob([new Uint8Array(part.data)]);
      const list = byName.get(part.filename);
      const piece = { index: part.index ?? 0, blob };
      if (list) list.push(piece);
      else byName.set(part.filename, [piece]);

      done += 1;
      if (!signal?.aborted) {
        onProgress?.({
          done,
          total,
          label: `ROM データを読み出し中 (${done}/${total})`,
        });
      }
      // 巨大 ArrayBuffer を連続処理すると UI が止まるので譲る
      await yieldToUi();
    }

    throwIfAborted(signal);
    const out = mergePieceBlobs(byName);

    const missing = record.partNames.filter((name) => !(name in out));
    if (missing.length > 0) {
      throw new Error(
        `ROM の一部が保存されていません: ${missing.join(', ')}\n` +
          'ライブラリから削除して、CUE と BIN をまとめて追加し直してください。',
      );
    }
    return out;
  }

  if (record.files && Object.keys(record.files).length > 0) {
    const out: Record<string, Blob> = {};
    const entries = Object.entries(record.files);
    let done = 0;
    for (const [name, data] of entries) {
      throwIfAborted(signal);
      out[name] = new Blob([new Uint8Array(data)]);
      done += 1;
      if (!signal?.aborted) {
        onProgress?.({
          done,
          total: entries.length,
          label: `ROM データを読み出し中 (${done}/${entries.length})`,
        });
      }
      await yieldToUi();
    }
    return out;
  }

  if (record.data) {
    throwIfAborted(signal);
    const meta = await getRomMeta(id);
    onProgress?.({ done: 1, total: 1, label: 'ROM データを読み出し中' });
    return { [meta?.filename ?? 'rom.bin']: new Blob([new Uint8Array(record.data)]) };
  }

  throw new Error('ROM データが見つかりません（削除された可能性があります）');
}

async function deleteRomParts(romId: string): Promise<void> {
  const parts = await getAllByIndex<RomPart>(STORE.romParts, 'romId', romId);
  if (parts.length === 0) return;
  await tx(STORE.romParts, 'readwrite', async (t) => {
    const store = t.objectStore(STORE.romParts);
    for (const p of parts) store.delete(p.id);
  });
}

/** 大容量向け指紋（サイズ + 先頭/末尾 64KB）。全文を読まない。 */
async function fingerprintBlob(blob: Blob): Promise<string> {
  const headLen = Math.min(blob.size, 64 * 1024);
  const tailStart = Math.max(0, blob.size - 64 * 1024);
  const head = new Uint8Array(await blob.slice(0, headLen).arrayBuffer());
  const tail = new Uint8Array(await blob.slice(tailStart, blob.size).arrayBuffer());
  const material = new Uint8Array(8 + head.length + tail.length);
  const view = new DataView(material.buffer);
  view.setUint32(0, blob.size >>> 0, true);
  view.setUint32(4, (blob.size / 0x100000000) >>> 0, true);
  material.set(head, 8);
  material.set(tail, 8 + head.length);
  return sha1(material.buffer);
}

/**
 * ディスクセットの指紋。
 * ファイル名は入れない（スマホで名前が変わると別物扱いになるため）。
 * CUE 本文も入れない（参照名の書き換えで端末ごとにバイト列が変わるため）。
 * データトラック（BIN/IMG 等）の内容指紋だけを並べてハッシュする。
 */
async function fingerprintDiscSet(entries: Array<{ name: string; source: Blob }>): Promise<string> {
  const data = entries.filter((e) => !isCueFilename(e.name));
  const targets = data.length > 0 ? data : entries;
  const parts: string[] = [];
  for (const e of targets) {
    parts.push(`${e.source.size}:${await fingerprintBlob(e.source)}`);
  }
  parts.sort();
  return sha1(new TextEncoder().encode(parts.join('\0')).buffer);
}

export async function listRoms(): Promise<RomMeta[]> {
  const roms = await getAll<RomMeta>(STORE.roms);
  return roms.sort((a, b) => (b.lastPlayedAt ?? b.addedAt) - (a.lastPlayedAt ?? a.addedAt));
}

export async function getRomMeta(id: string): Promise<RomMeta | undefined> {
  return get<RomMeta>(STORE.roms, id);
}

export async function findRomBySha1(sha1Hash: string): Promise<RomMeta | undefined> {
  const all = await getAll<RomMeta>(STORE.roms);
  return all.find((r) => r.sha1 === sha1Hash);
}

export async function getRomData(id: string): Promise<Uint8Array> {
  const files = await getRomFiles(id);
  const meta = await getRomMeta(id);
  const primary = meta?.filename;
  if (primary && files[primary]) return files[primary];
  const first = Object.values(files)[0];
  if (!first) throw new Error('ROM データが見つかりません（削除された可能性があります）');
  return first;
}

/**
 * ROM に紐づく全ファイル（basename → データ）。
 * 単一 ROM は主ファイル 1 件だけ。
 * 注意: 巨大セットではメモリを大きく使う。起動時は getRomFileBlobs を優先。
 */
export async function getRomFiles(id: string): Promise<Record<string, Uint8Array>> {
  const blobs = await getRomFileBlobs(id);
  const out: Record<string, Uint8Array> = {};
  for (const [name, blob] of Object.entries(blobs)) {
    out[name] = new Uint8Array(await blob.arrayBuffer());
  }
  return out;
}

/** 表示名の上限。長すぎると一覧が読めなくなるうえ、同期する JSON も膨らむ。 */
export const MAX_ROM_TITLE_LENGTH = 80;

/**
 * 表示名を変更する。
 *
 * 空文字は受け付けない。名前の無い項目はライブラリで見分けが付かず、
 * 削除するしかなくなるため。前後の空白は落とす。
 */
export async function renameRom(id: string, title: string): Promise<RomMeta> {
  const trimmed = title.trim();
  if (trimmed.length === 0) throw new Error('表示名を入力してください');
  if (trimmed.length > MAX_ROM_TITLE_LENGTH) {
    throw new Error(`表示名は ${MAX_ROM_TITLE_LENGTH} 文字までです`);
  }
  return updateRom(id, { title: trimmed, titleUpdatedAt: Date.now() });
}

export async function updateRom(id: string, patch: Partial<RomMeta>): Promise<RomMeta> {
  const meta = await getRomMeta(id);
  if (!meta) throw new Error('ROM が見つかりません');
  const next = { ...meta, ...patch, id };
  await put(STORE.roms, next);
  return next;
}

export async function markPlayed(id: string): Promise<void> {
  const meta = await getRomMeta(id);
  if (!meta) return;
  await put(STORE.roms, {
    ...meta,
    lastPlayedAt: Date.now(),
    playCount: meta.playCount + 1,
  });
}

export async function deleteRom(id: string): Promise<void> {
  // ROM 本体・セーブ・ステート・パートを漏れなく消す
  await tx(
    [STORE.roms, STORE.romData, STORE.romParts, STORE.saves, STORE.states, STORE.cheats],
    'readwrite',
    (t) => {
      t.objectStore(STORE.roms).delete(id);
      t.objectStore(STORE.romData).delete(id);
      t.objectStore(STORE.saves).delete(id);

      for (const store of [STORE.states, STORE.cheats, STORE.romParts]) {
        const index = t.objectStore(store).index('romId');
        const req = index.openKeyCursor(IDBKeyRange.only(id));
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) return;
          t.objectStore(store).delete(cursor.primaryKey);
          cursor.continue();
        };
      }
    },
  );

  const conflicts = await listConflicts();
  for (const c of conflicts) {
    if (c.romId === id) await clearConflict(c.id);
  }
}

/** 合計サイズ（File.size ベース。読み込み前の警告用）。 */
export function totalFileBytes(files: FileList | File[]): number {
  return Array.from(files).reduce((n, f) => n + (f.size || 0), 0);
}

/* -------------------------------------------------------------------------- */
/* BIOS                                                                        */
/* -------------------------------------------------------------------------- */

export interface BiosEntry {
  /** system + ファイル名で一意にする */
  id: string;
  system: SystemId;
  filename: string;
  sha1: string;
  size: number;
  data: ArrayBuffer;
  addedAt: number;
}

export async function addBios(file: File, system: SystemId): Promise<BiosEntry> {
  // pcsx_rearmed は 512 KiB 以外を BIOS と認めず HLE に落ちる。
  // ここで弾いておかないと、起動はするのにメモリーカードだけ壊れる。
  if (system === 'psx' && file.size !== 512 * 1024) {
    throw new Error(
      `PS1 BIOS は 512 KiB（524288 バイト）である必要があります（選択ファイルは ${file.size} バイト）。` +
        'scph5500.bin / scph5501.bin / scph5502.bin などの正規ダンプを選んでください。',
    );
  }
  const data = await file.arrayBuffer();
  const entry: BiosEntry = {
    id: `${system}:${file.name}`,
    system,
    filename: file.name,
    sha1: await sha1(data),
    size: file.size,
    data,
    addedAt: Date.now(),
  };
  await put(STORE.bios, entry);
  return entry;
}

export async function getBios(system: SystemId): Promise<Uint8Array | undefined> {
  const rec = await getBiosRecord(system);
  return rec?.data;
}

/** BIOS 本体と元ファイル名。pcsx は名前でリージョン候補を探す。 */
export async function getBiosRecord(
  system: SystemId,
): Promise<{ data: Uint8Array; filename: string } | undefined> {
  const all = await getAll<BiosEntry>(STORE.bios);
  const entry = all.find((b) => b.system === system);
  if (!entry) return undefined;
  return { data: new Uint8Array(entry.data), filename: entry.filename };
}

export async function listBios(): Promise<Array<Omit<BiosEntry, 'data'>>> {
  const all = await getAll<BiosEntry>(STORE.bios);
  return all.map(({ data: _data, ...rest }) => rest);
}

export async function deleteBios(id: string): Promise<void> {
  await del(STORE.bios, id);
}

/* -------------------------------------------------------------------------- */

function stripExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(0, i) : name;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[u]}`;
}
